import { PencilIcon, PlusIcon, StarIcon, XIcon } from "lucide-react";
import { useRef, useState } from "react";
import type { SavedDataView } from "~/views/viewDefinition";
import { cn } from "~/lib/utils";
import { planningDisplayRowClass } from "./planningDisplayChrome";

export function NewViewButton({
  onClick,
  disabled,
}: {
  readonly onClick: () => void;
  readonly disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title="Save the current state as a new view"
      className={planningDisplayRowClass({
        className: "pl-1 pr-2 disabled:pointer-events-none disabled:opacity-50",
      })}
    >
      <span className="flex size-3.5 shrink-0 items-center justify-center">
        <span className="flex size-3.5 items-center justify-center rounded-[3px] border border-dashed border-border/70">
          <PlusIcon className="size-2.5" />
        </span>
      </span>
      <span className="min-w-0 flex-1 truncate">New view</span>
    </button>
  );
}

export function BaselineViewRow({
  isActive,
  onSelect,
}: {
  readonly isActive: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isActive}
      className={planningDisplayRowClass({ selected: isActive, className: "pl-1 pr-2" })}
    >
      <span className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">Default view</span>
    </button>
  );
}

export function SavedViewRow({
  view,
  isActive,
  disabled,
  onSelect,
  onToggleDefault,
  onRename,
  onDelete,
}: {
  readonly view: SavedDataView;
  readonly isActive: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
  readonly onToggleDefault: () => void;
  readonly onRename: () => void;
  readonly onDelete: () => void;
}) {
  return (
    <div
      className={planningDisplayRowClass({
        selected: isActive,
        className: cn(
          "group/viewrow gap-1 pl-1 pr-1",
          disabled ? "pointer-events-none opacity-55" : null,
        ),
      })}
    >
      <button
        type="button"
        onClick={onToggleDefault}
        aria-label={
          view.isDefault ? `Clear default view (${view.name})` : `Set ${view.name} as default view`
        }
        title={view.isDefault ? "Default view — click to clear" : "Set as default view"}
        className={cn(
          "inline-flex size-3.5 shrink-0 items-center justify-center rounded-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          view.isDefault
            ? "text-primary hover:text-primary/80"
            : "text-muted-foreground/50 opacity-0 hover:text-foreground group-hover/viewrow:opacity-100 group-focus-within/viewrow:opacity-100",
        )}
      >
        <StarIcon className={cn("size-3", view.isDefault ? "fill-current" : null)} />
      </button>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={isActive}
        className="flex min-w-0 flex-1 items-center rounded-sm text-left text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="min-w-0 flex-1 truncate">{view.name}</span>
      </button>
      <div className="pointer-events-none flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/viewrow:pointer-events-auto group-hover/viewrow:opacity-100 group-focus-within/viewrow:pointer-events-auto group-focus-within/viewrow:opacity-100">
        <button
          type="button"
          onClick={onRename}
          aria-label={`Rename ${view.name}`}
          title="Rename"
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground/70 transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <PencilIcon className="size-3" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${view.name}`}
          title="Delete view"
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground/70 transition-colors hover:bg-destructive-surface hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <XIcon className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

export function ViewRenameForm({
  initialName,
  disabled,
  onCommit,
  onCancel,
}: {
  readonly initialName: string;
  readonly disabled: boolean;
  readonly onCommit: (name: string) => void;
  readonly onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initialName);
  const settled = useRef(false);
  const commit = () => {
    if (settled.current) return;
    settled.current = true;
    if (draft.trim().length > 0) onCommit(draft);
    else onCancel();
  };
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        commit();
      }}
      className="px-1"
    >
      <input
        aria-label={`Rename view (${initialName})`}
        value={draft}
        disabled={disabled}
        onChange={(event) => setDraft(event.currentTarget.value)}
        onFocus={(event) => event.currentTarget.select()}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          if (settled.current) return;
          settled.current = true;
          onCancel();
        }}
        autoFocus
        className="h-7 w-full min-w-0 rounded-sm border border-border bg-background px-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      />
    </form>
  );
}
